import React, { useState, useImperativeHandle, forwardRef, useContext, useMemo } from 'react';
import { Box, Typography, List, ListItem, IconButton, Divider, Tooltip, useTheme, Switch } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  DirectionsCar as DirectionsCarIcon,
  Handshake as HandshakeIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  MarkEmailRead,
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  PushPin,
  Beenhere,
  Person as PersonIcon,
  Book,
  NextPlan,
  Settings as SettingsIcon,
  AccountCircle,
  DarkMode as DarkModeIcon
} from '@mui/icons-material';
import UserMenu from '../userMenu/userMenu';
import { useTranslation } from 'react-i18next';
import ProfileDialog from '../userMenu/ProfileDialog';
import TenantSettingsModal from '../userMenu/tenantSettingsModal';
import { useThemeMode } from '../../context/themeContext';
import { AuthContext } from '../../context/authContext';
import { UserPlatformSubRole } from '../../models/user';
import { useNegotiationAlerts } from '../../context/negotiationAlertsContext';
import NavBadge from './NavBadge';
interface SidebarProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
}

export interface SidebarRef {
  openTenantSettings: (tab?: number) => void;
}

const Sidebar = forwardRef<SidebarRef, SidebarProps>(({ sidebarOpen, onToggleSidebar }, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const authContext = useContext(AuthContext);

  const navigate = useNavigate();
  const location = useLocation();
  const { totals: negotiationAlerts } = useNegotiationAlerts();
  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<number | undefined>(undefined);

  const handleOpenSettings = (tab?: number) => {
    setSettingsInitialTab(tab);
    setSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
    setSettingsInitialTab(undefined);
  };

  // Expose openTenantSettings method to parent via ref
  useImperativeHandle(ref, () => ({
    openTenantSettings: handleOpenSettings,
  }));

  // Get user's subrole
  const userSubRole = authContext?.user?.subRole;

  // Define which roles can see which navigation groups
  const canSeeOperations = userSubRole === UserPlatformSubRole.DIRECTOR ||
    userSubRole === UserPlatformSubRole.TRUCK_MANAGER;
  const canSeeResources = true; // Everyone can see resources (bookings/reservations/drivers)
  const canSeeAutomation = userSubRole === UserPlatformSubRole.DIRECTOR ||
    userSubRole === UserPlatformSubRole.TRUCK_MANAGER;

  const navigationGroups = useMemo<Array<{ id: string; label: string; items: any[] }>>(() => {
    const groups = [];

    // AI AUTOMATION - PRIMARY section for managers and directors (moved to top)
    if (canSeeAutomation) {
      groups.push({
        id: 'automation',
        label: t('AI AUTOMATION'),
        items: [
          { icon: SearchIcon, label: t('Load Search'), path: '/loadSearch', highlight: true },
          {
            icon: HandshakeIcon,
            label: t('Negotiations'),
            path: '/negotiations',
            badgeCount: negotiationAlerts.total,
            badgeTooltip: negotiationAlerts.total > 0
              ? t('{{unread}} unread emails · {{human}} need action', {
                unread: negotiationAlerts.unreadEmails,
                human: negotiationAlerts.needHuman,
              })
              : undefined,
          },
          { icon: DirectionsCarIcon, label: t('Fleet Monitoring'), path: '/monitoring' },
          { icon: NextPlan, label: t('Offer Query'), path: '/offerQuery' },
          { icon: DescriptionIcon, label: t('Smart Docs'), path: '/drag' },
          { icon: PushPin, label: t('Mail Automation'), path: '/mailReminder' },
          { icon: MarkEmailRead, label: t('Mail Offer Scanning'), path: '/mailScanOffers' },
        ],
      });
    }

    // Analytics & Overview - secondary section
    if (canSeeOperations) {
      groups.push({
        id: 'analytics',
        label: t('ANALYTICS'),
        items: [
          { icon: DashboardIcon, label: t('Dashboard'), path: '/dashboard' },
        ],
      });
    }

    // Resources - everyone can see
    if (canSeeResources) {
      groups.push({
        id: 'resources',
        label: t('RESOURCES'),
        items: [
          { icon: PersonIcon, label: t('Drivers'), path: '/drivers' },
          { icon: Beenhere, label: t('Bookings'), path: '/booking' },
          { icon: Book, label: t('Reservations'), path: '/reservation' },
        ],
      });
    }

    // Account - everyone can see profile, settings only for managers and directors
    groups.push({
      id: 'account',
      label: t('ACCOUNT'),
      items: [
        { icon: AccountCircle, label: t('Profile'), path: '#profile', isDialog: true, onClick: () => setProfileOpen(true) },
        ...(canSeeOperations ? [{ icon: SettingsIcon, label: t('Settings'), path: '#settings', isDialog: true, onClick: () => handleOpenSettings() }] : []),
      ],
    });

    // System - everyone can see
    groups.push({
      id: 'system',
      label: t('SYSTEM'),
      items: [
        { icon: DarkModeIcon, label: t('Dark mode'), path: '#darkmode', isThemeToggle: true, onClick: toggleTheme },
      ],
    });

    return groups;
  }, [canSeeOperations, canSeeResources, canSeeAutomation, t, negotiationAlerts]);

  const handleNavigation = (item: any) => {
    if (item.disabled) return;
    if (item.isThemeToggle && item.onClick) {
      item.onClick();
      return;
    }
    if (item.isDialog && item.onClick) {
      item.onClick();
      return;
    }
    navigate(item.path);
  };

  const isActive = (path: string) => {
    if (path.startsWith('#')) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const styles = {
    sidebar: {
      width: sidebarOpen ? 200 : 50,
      bgcolor: 'background.sidebar',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      borderRight: '1px solid',
      borderColor: 'border.light',
      boxShadow: '4px 0 12px rgba(0, 0, 0, 0.03)',
    },
    logoContainer: {
      pt: 2,
      pb: 1,
      px: sidebarOpen ? 1 : 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: sidebarOpen ? 'space-between' : 'center',
      position: 'relative',
      minHeight: 56,
      borderBottom: sidebarOpen ? '1px solid' : 'none',
      borderColor: sidebarOpen ? theme.palette.background.whiteLight : 'transparent',
    },
    logoBox: {
      width: 32,
      height: 32,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    logo: {
      width: '100%',
      height: '100%',
      objectFit: 'contain' as const,
    },
    toggleButton: {
      color: 'text.grey',
      backgroundColor: 'transparent',
      borderRadius: 2,
      transition: 'all 0.2s ease',
      '&:hover': {
        bgcolor: 'background.hoverGrey',
        color: 'text.primary',
        transform: sidebarOpen ? 'scale(1.05)' : 'translateX(50%)',
      },
      '&:active': {
        bgcolor: 'background.inputGrey',
        transform: sidebarOpen ? 'scale(0.98)' : 'translateX(50%)',
      },
      '& .MuiTouchRipple-root': {
        display: 'none',
      },
      position: sidebarOpen ? 'static' : 'absolute',
      right: sidebarOpen ? 'auto' : '50%',
      transform: sidebarOpen ? 'none' : 'translateX(50%)',
    },
    navigation: {
      flex: 1,
      p: sidebarOpen ? 1 : 0.5,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'border.light',
        borderRadius: '100px',
      },
    },
    groupLabel: {
      px: sidebarOpen ? -2 : 0,
      py: sidebarOpen ? 0.5 : 0.5,
      mt: sidebarOpen ? 1.5 : 1.5,
      mb: 0.25,
      '&:first-of-type': {
        mt: 0,
      },
      display: 'flex',
      alignItems: 'center',
      justifyContent: sidebarOpen ? 'flex-start' : 'center',
      minHeight: sidebarOpen ? 'auto' : 20,
    },
    groupLabelText: {
      fontSize: (theme: any) => theme.typography.fontSizes.caption,
      fontWeight: 600,
      color: 'text.grey',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    navItem: {
      mb: 0.25,
      borderRadius: 2,
      bgcolor: 'transparent',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        bgcolor: (theme: any) => theme.palette.mode === 'dark' ? 'background.inputGrey' : '#EDE7F0',
      },
      cursor: 'pointer',
      px: sidebarOpen ? 0.75 : 0,
      py: 0.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: sidebarOpen ? 'flex-start' : 'center',
      height: 36,
      width: sidebarOpen ? '100%' : 40,
      mx: sidebarOpen ? 0 : 'auto',
    },
    navItemActive: {
      bgcolor: (theme: any) => theme.palette.mode === 'dark' ? '#261F2D' : '#E5D9EB',
      '&:hover': {
        bgcolor: (theme: any) => theme.palette.mode === 'dark' ? '#2D2535' : '#D8C8E0',
      },
    },
    navContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      width: '100%',
      gap: 0.75,
    },
    navContentCollapsed: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
    },
    navText: {
      fontWeight: 500,
      fontSize: (theme: any) => theme.typography.fontSizes.body,
    },
    navTextActive: {
      fontWeight: 500,
      color: (theme: any) => theme.palette.mode === 'dark' ? '#ffffff' : 'text.purple',
      fontSize: (theme: any) => theme.typography.fontSizes.body,
    },
    navTextInactive: {
      fontWeight: 500,
      color: 'text.grey',
      fontSize: (theme: any) => theme.typography.fontSizes.body,
    },
    navItemDisabled: {
      mb: 0.25,
      borderRadius: 2,
      bgcolor: 'transparent',
      cursor: 'not-allowed',
      opacity: 0.5,
      px: sidebarOpen ? 1 : 0,
      py: 0.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: sidebarOpen ? 'flex-start' : 'center',
      height: 36,
      width: sidebarOpen ? '100%' : 40,
      mx: sidebarOpen ? 0 : 'auto',
    },
    navContentDisabled: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      width: '100%',
      gap: 0.75,
    },
    navContentDisabledCollapsed: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
    navTextDisabled: {
      fontWeight: 500,
      color: 'text.light',
      fontSize: (theme: any) => theme.typography.fontSizes.small,
    },
    userProfile: {
      px: 1.5,
      pb: 1,
      pt: 1.5,
      borderTop: '1px solid border.default',
    },
    userContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    userContentCollapsed: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    userInfo: {
      ml: 2,
    },
    userName: {
      fontWeight: 600,
      color: 'text.grey',
      fontSize: (theme: any) => theme.typography.fontSizes.body,
    },
    userRole: {
      color: 'text.grey',
      fontSize: (theme: any) => theme.typography.fontSizes.caption,
    },
  };

  return (
    <Box sx={styles.sidebar}>
      <Box sx={styles.logoContainer}>
        {sidebarOpen && (
          <Box sx={styles.logoBox}>
            <Box
              component="img"
              src={mode === 'dark' ? '/white-logo.png' : '/black-logo.png'}
              alt="Company Logo"
              sx={styles.logo}
            />
          </Box>
        )}
        <IconButton
          onClick={onToggleSidebar}
          sx={styles.toggleButton}
          disableRipple
        >
          {sidebarOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      <Box sx={styles.navigation}>
        <List sx={{ py: 0, px: 0, width: '100%' }}>
          {navigationGroups.map((group, groupIndex) => (
            <React.Fragment key={group.id}>
              <Box sx={styles.groupLabel}>
                {sidebarOpen ? (
                  <Typography sx={styles.groupLabelText}>
                    {group.label}
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      bgcolor: 'text.light',
                      opacity: 0.3,
                    }}
                  />
                )}
              </Box>

              {group.items.map((item, itemIndex) => {
                const active = isActive(item.path);
                const IconComponent = item.icon;
                if ('isThemeToggle' in item && item.isThemeToggle) {
                  const themeToggleContent = (
                    <ListItem
                      key={`${group.id}-${itemIndex}`}
                      sx={{
                        ...styles.navItem,
                        justifyContent: sidebarOpen ? 'space-between' : 'center',
                        cursor: 'pointer',
                      }}
                      disablePadding
                      onClick={() => {
                        if (!sidebarOpen) {
                          toggleTheme();
                        }
                      }}
                    >
                      {sidebarOpen ? (
                        <>
                          <Box sx={styles.navContent}>
                            <IconComponent
                              sx={{
                                fontSize: {
                                  xs: '1.25rem',
                                  sm: '1.25rem',
                                  md: '1.25rem',
                                  lg: '1.25rem',
                                  xl: '1.25rem',
                                },
                                width: {
                                  xs: '1.25rem',
                                  sm: '1.25rem',
                                  md: '1.25rem',
                                  lg: '1.25rem',
                                  xl: '1.25rem',
                                },
                                height: {
                                  xs: '1.25rem',
                                  sm: '1.25rem',
                                  md: '1.25rem',
                                  lg: '1.25rem',
                                  xl: '1.25rem',
                                },
                                color: mode === 'dark' ? 'text.purple' : 'text.grey',
                                opacity: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0,
                              }}
                            />
                            <Typography sx={styles.navTextInactive}>
                              {item.label}
                            </Typography>
                          </Box>
                          <Switch
                            checked={mode === 'dark'}
                            onChange={toggleTheme}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </>
                      ) : (
                        <Box sx={styles.navContentCollapsed}>
                          <IconComponent
                            sx={{
                              fontSize: {
                                xs: '1.25rem',
                                sm: '1.25rem',
                                md: '1.25rem',
                                lg: '1.25rem',
                                xl: '1.25rem',
                              },
                              width: {
                                xs: '1.25rem',
                                sm: '1.25rem',
                                md: '1.25rem',
                                lg: '1.25rem',
                                xl: '1.25rem',
                              },
                              height: {
                                xs: '1.25rem',
                                sm: '1.25rem',
                                md: '1.25rem',
                                lg: '1.25rem',
                                xl: '1.25rem',
                              },
                              color: mode === 'dark' ? 'text.purple' : 'text.grey',
                              opacity: 0.8,
                              display: 'flex',
                              alignItems: 'center',
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      )}
                    </ListItem>
                  );

                  if (!sidebarOpen) {
                    return (
                      <Tooltip
                        key={`${group.id}-${itemIndex}`}
                        title={item.label}
                        placement="right"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#333333',
                              color: '#ffffff',
                              fontSize: (theme: any) => theme.typography.fontSizes.body,
                              fontWeight: 500,
                              px: 1.5,
                              py: 0.75,
                            },
                          },
                          arrow: {
                            sx: {
                              color: theme.palette.mode === 'dark' ? '#1a1a1a' : '#333333',
                            },
                          },
                        }}
                      >
                        {themeToggleContent}
                      </Tooltip>
                    );
                  }

                  return themeToggleContent;
                }

                const badgeCount: number = typeof item.badgeCount === 'number' ? item.badgeCount : 0;
                const badgeTooltip: string | undefined = typeof item.badgeTooltip === 'string' ? item.badgeTooltip : undefined;

                const navItemContent = (
                  <ListItem
                    key={`${group.id}-${itemIndex}`}
                    sx={{
                      ...styles.navItem,
                      ...(active && styles.navItemActive),
                      ...('disabled' in item && item.disabled && styles.navItemDisabled as any),
                      overflow: 'visible',
                    }}
                    disablePadding
                    onClick={() => handleNavigation(item)}>
                    <Box sx={sidebarOpen ? styles.navContent : { ...styles.navContentCollapsed, position: 'relative' }}>
                      <IconComponent
                        sx={{
                          fontSize: {
                            xs: '1.25rem',
                            sm: '1.25rem',
                            md: '1.25rem',
                            lg: '1.25rem',
                            xl: '1.25rem',
                          },
                          width: {
                            xs: '1.25rem',
                            sm: '1.25rem',
                            md: '1.25rem',
                            lg: '1.25rem',
                            xl: '1.25rem',
                          },
                          height: {
                            xs: '1.25rem',
                            sm: '1.25rem',
                            md: '1.25rem',
                            lg: '1.25rem',
                            xl: '1.25rem',
                          },
                          color: active
                            ? (theme.palette.mode === 'dark' ? '#8b5cf6' : 'text.purple')
                            : ('disabled' in item && item.disabled ? 'text.light' : 'text.grey'),
                          opacity: active ? 1 : 0.8,
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                        }}
                      />
                      {sidebarOpen && (
                        <Typography
                          sx={
                            'disabled' in item && item.disabled
                              ? styles.navTextDisabled
                              : active
                                ? styles.navTextActive
                                : styles.navTextInactive
                          }>
                          {item.label}
                        </Typography>
                      )}
                      {sidebarOpen && (
                        <NavBadge count={badgeCount} label={badgeTooltip} />
                      )}
                      {!sidebarOpen && (
                        <NavBadge count={badgeCount} label={badgeTooltip} compact floating />
                      )}
                    </Box>
                  </ListItem>
                );

                if (!sidebarOpen) {
                  return (
                    <Tooltip
                      key={`${group.id}-${itemIndex}`}
                      title={item.label}
                      placement="right"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#333333',
                            color: '#ffffff',
                            fontSize: (theme: any) => theme.typography.fontSizes.body,
                            fontWeight: 500,
                            px: 1.5,
                            py: 0.75,
                          },
                        },
                        arrow: {
                          sx: {
                            color: theme.palette.mode === 'dark' ? '#1a1a1a' : '#333333',
                          },
                        },
                      }}
                    >
                      {navItemContent}
                    </Tooltip>
                  );
                }

                return navItemContent;
              })}

              {sidebarOpen && groupIndex < navigationGroups.length - 1 && (
                <Divider sx={{ mx: 1.5, my: 1.5, borderColor: 'border.light' }} />
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      <ProfileDialog isOpen={profileOpen} setIsOpen={setProfileOpen} onClose={() => setProfileOpen(false)} />

      <TenantSettingsModal
        open={settingsOpen}
        onClose={handleCloseSettings}
        setOpen={setSettingsOpen}
        initialTab={settingsInitialTab}
      />
      <Box sx={styles.userProfile}>
        <UserMenu showUserInfo={sidebarOpen} />
      </Box>
    </Box>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
